import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { FlorenciaChat } from "./components/chat";
import { useMetaPixelPageView } from "./hooks/useMetaPixelPageView";

// Lazy-loaded routes — keeps initial bundle small
const Reservas = lazy(() => import("./pages/Reservas"));
const Formulario = lazy(() => import("./pages/Formulario"));
const BrandGuide = lazy(() => import("./pages/BrandGuide"));
const EmployeeDashboard = lazy(() => import("./pages/EmployeeDashboard").catch(() => {
  window.location.reload();
  return import("./pages/EmployeeDashboard");
}));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard").catch(() => {
  // Retry once on stale HMR module fetch failure
  window.location.reload();
  return import("./pages/AdminDashboard");
}));
const AdminClientFunnelView = lazy(() => import("./pages/AdminClientFunnelView"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Cotizador = lazy(() => import("./pages/Cotizador"));
const ClientDetailPage = lazy(() => import("./pages/ClientDetailPage"));
const REAZone = lazy(() => import("./pages/REAZone"));
const ClientPresentationPrint = lazy(() => import("./pages/ClientPresentationPrint"));
const PdfQA = lazy(() => import("./pages/PdfQA"));
const QuotePresentationPage = lazy(() => import("./pages/QuotePresentationPage"));
const FullPresentationPage = lazy(() => import("./pages/FullPresentationPage"));
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ProjectPresentation = lazy(() => import("./pages/ProjectPresentation"));
const TestingPage = lazy(() => import("./pages/TestingPage"));
const MortgageAuditor = lazy(() => import("./pages/MortgageAuditor"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,       // 2 min — avoid refetch on every mount
      gcTime: 10 * 60 * 1000,          // 10 min cache
      refetchOnWindowFocus: false,      // stop aggressive refetching on tab switch
      retry: (failureCount, error: Error & { status?: number; code?: number }) => {
        // Don't retry auth errors (401/403) — they cause loops
        const status = error?.status ?? error?.code;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
    },
  },
});

// SPA PageView tracker — must be inside BrowserRouter
const MetaPixelTracker = () => {
  useMetaPixelPageView();
  return null;
};

// Wrapper component that conditionally renders Florencia based on route
const FlorenciaWrapper = () => {
  const location = useLocation();
  // Hide Florencia on presentation routes (/p/:slug)
  const isPresentationRoute = location.pathname.startsWith('/p/') || location.pathname.includes('/presentacion/');
  
  if (isPresentationRoute) {
    return null;
  }
  
  return <FlorenciaChat />;
};

// Minimal loading fallback
const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/reservas" element={<Reservas />} />
              <Route path="/formulario" element={<Formulario />} />
              <Route path="/brand-guide" element={<BrandGuide />} />
              <Route path="/empleados" element={<EmployeeDashboard />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/clientes/:clienteId/funnel" element={<AdminClientFunnelView />} />
              <Route path="/proyecto/:slug" element={<ProjectDetail />} />
              <Route path="/cotizador" element={<Cotizador />} />
              <Route path="/clientes/:clienteId" element={<ClientDetailPage />} />
              <Route path="/clientes/:clientId/presentaciones/:projectId/print" element={<ClientPresentationPrint />} />
              <Route path="/clientes/:clienteId/cotizador/presentacion/cotizacion" element={<QuotePresentationPage />} />
              <Route path="/clientes/:clienteId/cotizador/presentacion/completa" element={<FullPresentationPage />} />
              <Route path="/reazone" element={<REAZone />} />
              <Route path="/buscar" element={<REAZone />} />
              <Route path="/tinderea" element={<REAZone />} />
              <Route path="/quiz" element={<REAZone />} />
              <Route path="/pdf-qa" element={<PdfQA />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/p/:slug" element={<ProjectPresentation />} />
              <Route path="/testing" element={<TestingPage />} />
              <Route path="/auditor-hipotecario" element={<MortgageAuditor />} />
              <Route path="/portal/:token" element={<ClientPortal />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <MetaPixelTracker />
          <FlorenciaWrapper />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
